/* eslint-disable react-hooks/exhaustive-deps */
import { ComponentCommonAudio, PodcastListeningTimeEntity } from '@/graphql/generated/graphql';
import './style.css';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PauseIcon, PlayIcon, SpeakerWaveIcon, SpeakerXMarkIcon, TableCellsIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';

const DefaultContentTemplateAudio = forwardRef(
  (
    {
      audio,
      className,
      onEnded,
      handleTimeUpdate,
      listeningTime,
      isDefaultContentTemplate = false,
      onPlayAudio,
    }: {
      audio: ComponentCommonAudio;
      className?: string;
      onEnded?: () => void;
      handleTimeUpdate?: (audio: HTMLAudioElement) => void;
      listeningTime?: PodcastListeningTimeEntity;
      isDefaultContentTemplate?: boolean;
      onPlayAudio?: () => void;
    },
    ref,
  ) => {
    const audioRef = useRef(new Audio(audio?.audio?.data?.attributes?.url || ''));
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isTranscriptionOpen, setTranscriptionOpen] = useState(false);
    const lastSentTimeRef = useRef(0); // Ref to store last sent time

    useImperativeHandle(ref, () => ({
      play: () => audioRef.current.play(),
      pause: () => audioRef.current.pause(),
      togglePlayPause: () => togglePlayPause(),
      setCurrentTime: (time: number) => setCurrentTime(time),
      setMuted: (muted: boolean) => setIsMuted(muted),
    }));

    const togglePlayPause = () => {
      const prevValue = isPlaying;
      setIsPlaying(!prevValue);
      if (!prevValue) {
        audioRef.current.play();
        onPlayAudio && onPlayAudio();
      } else {
        audioRef.current.pause();
      }
    };

    const onPlaying = () => {
      const timeFloor = Math.floor(audioRef.current.currentTime);
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);

      if (timeFloor % 5 === 0 && timeFloor !== lastSentTimeRef.current && handleTimeUpdate) {
        handleTimeUpdate(audioRef.current);
        lastSentTimeRef.current = timeFloor;
      }
    };

    const onScrub = (value: number) => {
      setCurrentTime(value);
      audioRef.current.currentTime = value;
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    };

    const onToggleMute = () => {
      setIsMuted(!isMuted);
      audioRef.current.muted = !isMuted;
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (handleTimeUpdate) handleTimeUpdate(audioRef.current);

      if (onEnded) {
        onEnded();
      }
    };

    const onTranscriptionOpen = () => {
      setTranscriptionOpen(!isTranscriptionOpen);
    };

    const formatTime = (time: number) => {
      return `${Math.floor(time / 60)}:${`0${Math.floor(time % 60)}`.slice(-2)}`;
    };

    useEffect(() => {
      isPlaying ? audioRef.current.play() : audioRef.current.pause();
    }, [isPlaying]);

    useEffect(() => {
      audioRef.current.addEventListener('timeupdate', onPlaying);
      audioRef.current.addEventListener('ended', handleEnded);
      return () => {
        audioRef.current.removeEventListener('timeupdate', onPlaying);
        audioRef.current.removeEventListener('ended', handleEnded);
      };
    }, [audioRef]);

    useEffect(() => {
      if (listeningTime) {
        audioRef.current.currentTime = listeningTime.attributes?.listeningTime;
        setCurrentTime(listeningTime.attributes?.listeningTime);
        setDuration(listeningTime.attributes?.duration);
      }
    }, []);

    useEffect(() => {
      const url = audio?.audio?.data?.attributes?.url;
      if (!url) return;

      const getAudioDuration = (url: string) => {
        return new Promise((resolve, reject) => {
          const audio = new Audio();
          audio.onloadedmetadata = function () {
            resolve(audio.duration);
          };
          audio.onerror = function () {
            reject('Error loading audio file');
          };
          audio.src = url;
        });
      };

      getAudioDuration(url)
        .then((duration) => setDuration(duration as number))
        .catch((error) => console.error(error));
    }, [audio?.audio?.data?.attributes?.url]);

    return (
      <>
        <div
          className={cn(
            'flex flex-col items-start rounded-2xl border border-primary-500 bg-white px-5 py-6',
            className,
            {
              'my-0': isDefaultContentTemplate,
              'my-sh-30': !isDefaultContentTemplate,
            },
          )}>
          <div className="flex w-full justify-between">
            <div className="flex w-full items-center gap-2">
              <button onClick={togglePlayPause} className="hover:opacity-70 focus:outline-none">
                {isPlaying ? (
                  <PauseIcon className=" size-4 text-[#488FE3]" />
                ) : (
                  <PlayIcon className=" size-4 text-[#488FE3]" />
                )}
              </button>
              <div className=" text-sh-xss">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={onToggleMute} className="hover:opacity-70 focus:outline-none">
                {isMuted ? (
                  <SpeakerXMarkIcon className=" size-4 text-[#488FE3]" />
                ) : (
                  <SpeakerWaveIcon className=" size-4 text-[#488FE3]" />
                )}
              </button>

              {audio?.transcription ? (
                <button onClick={onTranscriptionOpen} className="hover:opacity-70 focus:outline-none">
                  <TableCellsIcon className=" size-4 text-[#488FE3]" />
                </button>
              ) : null}
            </div>
          </div>
          <div className=" w-full">
            <input
              type="range"
              value={currentTime}
              step="1"
              min="0"
              max={duration}
              className="h-1 w-full cursor-pointer appearance-none rounded-lg bg-[#488FE3]"
              onChange={(e) => onScrub(parseFloat(e.target.value))}
            />
          </div>
        </div>
        {audio?.transcription ? (
          <div
            className={` overflow-y-auto transition-all duration-500 ease-in-out ${isTranscriptionOpen ? 'h-64' : 'h-0'}`}>
            <p
              dangerouslySetInnerHTML={{
                __html: audio?.transcription || '',
              }}></p>
          </div>
        ) : null}
      </>
    );
  },
);

DefaultContentTemplateAudio.displayName = 'DefaultContentTemplateAudio';

export default DefaultContentTemplateAudio;
